/**
 * useAdminMutations Hook
 *
 * React Query mutation hooks for admin CRUD operations.
 *
 * @see Sprint 20 - Interface Super Admin /bill-admin
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  adminService,
  type CreateAdminCompanyInput,
  type UpdateAdminCompanyInput,
  type CreateAdminBrandInput,
  type UpdateAdminBrandInput,
  type UpsertAdminPosConfigInput,
} from '../services/admin.service';
import { adminCompanyKeys, adminBrandKeys } from './useAdminCompanies';
import { coreApiClient } from '@/api/core-client';

type ApiError = Error & { response?: { data?: { error?: { message?: string } } } };

// =============================================================================
// COMPANY MUTATIONS
// =============================================================================

/**
 * Hook to create a company
 */
export function useCreateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAdminCompanyInput) => adminService.createCompany(data),
    onSuccess: (company) => {
      queryClient.invalidateQueries({ queryKey: adminCompanyKeys.all });
      toast.success(`Entreprise "${company.name}" creee avec succes`);
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update a company
 */
export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdminCompanyInput }) =>
      adminService.updateCompany(id, data),
    onSuccess: (company) => {
      queryClient.invalidateQueries({ queryKey: adminCompanyKeys.all });
      toast.success(`Entreprise "${company.name}" mise a jour`);
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to deactivate a company
 */
export function useDeactivateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.deactivateCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCompanyKeys.all });
      toast.success('Entreprise desactivee');
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la desactivation');
    },
  });
}

// =============================================================================
// BRAND MUTATIONS
// =============================================================================

/**
 * Hook to create a brand
 */
export function useCreateBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      data,
    }: {
      companyId: string;
      data: CreateAdminBrandInput;
    }) => adminService.createBrand(companyId, data),
    onSuccess: (brand) => {
      queryClient.invalidateQueries({ queryKey: adminCompanyKeys.all });
      queryClient.invalidateQueries({ queryKey: adminBrandKeys.all });
      toast.success(`Brand "${brand.name}" cree avec succes`);
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update a brand
 */
export function useUpdateBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdminBrandInput }) =>
      adminService.updateBrand(id, data),
    onSuccess: (brand) => {
      queryClient.invalidateQueries({ queryKey: adminCompanyKeys.all });
      queryClient.invalidateQueries({ queryKey: adminBrandKeys.all });
      toast.success(`Brand "${brand.name}" mis a jour`);
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to deactivate a brand
 */
export function useDeactivateBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.deactivateBrand(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCompanyKeys.all });
      queryClient.invalidateQueries({ queryKey: adminBrandKeys.all });
      toast.success('Brand desactive');
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la desactivation');
    },
  });
}

// =============================================================================
// POS CONFIG MUTATIONS
// =============================================================================

/**
 * Hook to create/update POS config
 */
export function useUpsertPosConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      brandId,
      data,
    }: {
      brandId: string;
      data: UpsertAdminPosConfigInput;
    }) => adminService.upsertPosConfig(brandId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCompanyKeys.all });
      queryClient.invalidateQueries({ queryKey: adminBrandKeys.all });
      toast.success('Configuration POS enregistree');
    },
    onError: (error: ApiError) => {
      toast.error(
        error.response?.data?.error?.message ?? 'Erreur lors de la sauvegarde'
      );
    },
  });
}

/**
 * Hook to delete POS config
 */
export function useDeletePosConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (brandId: string) => adminService.deletePosConfig(brandId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCompanyKeys.all });
      queryClient.invalidateQueries({ queryKey: adminBrandKeys.all });
      toast.success('Configuration POS supprimee');
    },
    onError: (error: ApiError) => {
      toast.error(
        error.response?.data?.error?.message ?? 'Erreur lors de la suppression'
      );
    },
  });
}

// =============================================================================
// COMPANY LOGO MUTATIONS
// =============================================================================

/**
 * Hook to upload a company logo
 */
export function useUploadCompanyLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, file }: { companyId: string; file: File }) => {
      const formData = new FormData();
      formData.append('logo', file);
      return coreApiClient.post(
        `/admin/companies/${companyId}/logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCompanyKeys.all });
      toast.success('Logo mis a jour');
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? "Erreur lors de l'upload du logo");
    },
  });
}

/**
 * Hook to delete a company logo
 */
export function useDeleteCompanyLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyId: string) =>
      coreApiClient.delete(`/admin/companies/${companyId}/logo`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCompanyKeys.all });
      toast.success('Logo supprime');
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la suppression du logo');
    },
  });
}

// =============================================================================
// SALES SYNC MUTATIONS
// =============================================================================

/**
 * Hook to initialize sales sync (tare)
 */
export function useInitSalesSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ brandId, startDate }: { brandId: string; startDate: string }) =>
      adminService.initSalesSync(brandId, startDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminBrandKeys.all });
      toast.success('Synchronisation initialisee avec succes');
    },
    onError: (error: ApiError) => {
      toast.error(
        error.response?.data?.error?.message ?? 'Erreur lors de l\'initialisation'
      );
    },
  });
}

/**
 * Hook to unblock a stuck sales sync batch
 */
export function useUnblockSalesSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (brandId: string) => adminService.unblockSalesSync(brandId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminBrandKeys.all });
      toast.success('Batch debloque avec succes');
    },
    onError: (error: ApiError) => {
      toast.error(
        error.response?.data?.error?.message ?? 'Erreur lors du deblocage'
      );
    },
  });
}
